import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import './LanguageChooserModal.css';

interface LanguageChooserModalProps {
  isOpen: boolean;
  brand: 'Ford' | 'Lincoln';
  languages: string[];
  currentLocale: string;
  getLanguageLabel: (locale: string) => { text: string; lang?: string };
  titleText: string;
  /** `lang` for the title element — omitted (not defaulted) when
   * `currentLocale` isn't one the caller actually has a translation for;
   * see this component's own comment below. */
  titleLang?: string;
  onSelect: (locale: string) => void;
  onClose: () => void;
  /** The footer's "Change Language" button — focus returns here on close. */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

/**
 * Full-screen language chooser (SA-1681 fix-08) — replaces the small
 * dropdown footers used to show. Design source: the per-frame modal
 * markup in `Screen 01 - About You (Web).dc.html` (Ford: centered column,
 * blue buttons; Lincoln: top-aligned, uppercase title, coral-underlined
 * buttons). Rendered in a portal to `document.body` so the footer's own
 * full-bleed CSS (`width:100vw` breakout, see GlobalFooter.css) and
 * `.app`/`.layout-base`'s stacking context can never clip or reposition
 * it — the brief's alternative (`position:fixed` at the end of the
 * footer) would work too, but a portal sidesteps that risk entirely.
 *
 * a11y (WCAG 2.1 AA): `role="dialog" aria-modal="true"
 * aria-labelledby=<title id>`; opens with focus on the CURRENT locale's
 * button; Escape and a backdrop click both close; focus returns to the
 * trigger button on close; body scroll is locked while open; a minimal
 * Tab/Shift+Tab wrap keeps focus inside the dialog (there are only 2-3
 * buttons, so a full focus-trap library is unwarranted here).
 *
 * `lang` on the title and each option (Codex review round 2, WCAG
 * 3.1.2): this modal is portaled to `document.body`, which only ever
 * carries `index.html`'s `lang="en"` — without an explicit override, a
 * Spanish/French title (or Lincoln's native-language "Español" option
 * label) reads as English to assistive tech. The title's `lang` is the
 * caller-supplied `titleLang` — NOT `currentLocale` directly (round 3):
 * `currentLocale` reaches the footer unfiltered from `Survey.tsx`, and an
 * unsupported code has no real translation, so the caller passes
 * `titleLang={undefined}` for it rather than mistagging whatever text
 * actually rendered. Each option's `lang` is likewise optional and NOT
 * the locale that button represents — it's whatever language ITS LABEL
 * TEXT is actually written in (see `getLanguageLabel`'s own comment in
 * GlobalFooter.tsx: Ford's "Spanish" is an English word, Lincoln's
 * "Español" is a Spanish word, and an unrecognized locale's raw code is
 * neither, so it comes through with `lang` omitted too).
 */
const LanguageChooserModal: React.FC<LanguageChooserModalProps> = ({
  isOpen,
  brand,
  languages,
  currentLocale,
  getLanguageLabel,
  titleText,
  titleLang,
  onSelect,
  onClose,
  triggerRef,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Codex/Opus review round 3: a hardcoded id would collide across
  // multiple mounted instances (a duplicate id makes aria-labelledby
  // ambiguous, and the round-2 lang work made this element the dialog's
  // accessible name) -- same useId() pattern GlobalHeader.tsx's flyout
  // already uses for its own heading id.
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Capture the trigger now: the ref could point elsewhere by the time the cleanup runs.
    const trigger = triggerRef.current;

    const dialog = dialogRef.current;
    const current = dialog?.querySelector<HTMLButtonElement>('[aria-current="true"]');
    (current || dialog?.querySelector<HTMLButtonElement>('button'))?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialog) {
        const focusable = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button'));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      trigger?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="lang-modal-backdrop" data-brand={brand.toLowerCase()} onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="lang-modal-content"
        onClick={e => e.stopPropagation()}
      >
        <div id={titleId} className="lang-modal-title" lang={titleLang}>
          {titleText}
        </div>
        {languages.map(locale => {
          const isCurrent = locale === currentLocale;
          const { text, lang } = getLanguageLabel(locale);
          return (
            <button
              key={locale}
              type="button"
              className="lang-modal-option"
              aria-current={isCurrent ? 'true' : undefined}
              lang={lang}
              onClick={() => onSelect(locale)}
            >
              {text}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
};

export default LanguageChooserModal;
